import {FormControl, FormField} from 'platform/components';
import {Box, HStack, Heading, Space, VStack} from 'platform/foundation';

import {UseFormReturn, useWatch} from 'react-hook-form';
import {useLocation} from 'react-router-dom';

import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';
import {SettingsFooter, getOrderPaymentTypeOptions} from '@dms/settings/shared';

import {useNavigate} from 'shared/web';

import {FormType} from '../../types/ServiceOrderVariant';
import {getServiceOrderVariantsRoutes} from '../../utils/getServiceOrderVariantsRoutes';
import {InternalPayment} from './components/InternalPayment';
import {PaymentType} from './components/PaymentType';

interface PaymentTabProps {
  control: FormControl<FormType>;
  formApi: UseFormReturn<FormType>;
}

export function PaymentTab(props: PaymentTabProps) {
  const {pathname} = useLocation();
  const navigate = useNavigate();
  const serviceOrderVariantsRoutes = getServiceOrderVariantsRoutes(pathname);

  const paymentType = useWatch({control: props.control, name: 'paymentType'});
  const orderPaymentTypeOptions = getOrderPaymentTypeOptions(paymentType);

  return (
    <>
      <VStack spacing={4}>
        <Heading size={4}>{i18n.t('entity.bank.labels.paymentType')}</Heading>
        <PaymentType
          formApi={props.formApi}
          control={props.control}
          title={i18n.t('entity.invoice.paymentMethod.cash')}
          formSection="paymentType.cashPayment"
          data-testid={testIds.settings.serviceOrderVariantsDetail('cashPayment')}
        />
        <PaymentType
          formApi={props.formApi}
          control={props.control}
          title={i18n.t('entity.invoice.paymentMethod.card')}
          formSection="paymentType.cardPayment"
          data-testid={testIds.settings.serviceOrderVariantsDetail('cardPayment')}
        />
        <PaymentType
          formApi={props.formApi}
          control={props.control}
          title={i18n.t('entity.invoice.paymentMethod.bankTransfer')}
          formSection="paymentType.bankPayment"
          data-testid={testIds.settings.serviceOrderVariantsDetail('bankPayment')}
        />
        <InternalPayment
          formApi={props.formApi}
          control={props.control}
          title={i18n.t('general.labels.internal')}
          data-testid={testIds.settings.serviceOrderVariantsDetail('internalPayment')}
        />
        <HStack spacing={4}>
          <Box flex={1}>
            <FormField
              control={props.control}
              name="paymentType.defaultPaymentType"
              type="choice"
              label={i18n.t('entity.order.labels.defaultPaymentMethod')}
              options={orderPaymentTypeOptions}
              data-testid={testIds.settings.serviceOrderVariantsDetail('defaultPayment')}
              isDisabled={!orderPaymentTypeOptions.length}
            />
          </Box>
          <Space fillAvailable />
        </HStack>
      </VStack>
      <SettingsFooter
        actions={[
          {
            type: 'button',
            variant: 'secondary',
            title: i18n.t('general.actions.discardChanges'),
            onClick: () => navigate(serviceOrderVariantsRoutes.overview),
          },
          {
            type: 'form-button',
            control: props.control,
            buttonType: 'submit',
            variant: 'primary',
            title: i18n.t('general.actions.saveChanges'),
          },
        ]}
        data-testid={testIds.settings.serviceOrderVariantsDetail('footer')}
      />
    </>
  );
}
